import React, { useEffect, useState, useMemo } from "react";
import { ChevronDown, SlidersHorizontal, RotateCcw, X } from "lucide-react";
import {
  ISearchFiltersResponse,
  ISearchProductsRequest,
} from "../../types/Request/CustomerRequest";

interface FilterSidebarProps {
  filters?: ISearchFiltersResponse;
  searchRequest: ISearchProductsRequest;
  onChange: (request: ISearchProductsRequest) => void;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  filters,
  searchRequest,
  onChange,
}) => {
  const [priceRange, setPriceRange] = useState<number>(
    searchRequest.maxPrice ?? filters?.priceRange?.maxPrice ?? 0
  );

  useEffect(() => {
    setPriceRange(
      searchRequest.maxPrice ?? filters?.priceRange?.maxPrice ?? 0
    );
  }, [searchRequest.maxPrice, filters]);

  // Normalized number arrays for type-safe comparisons and outputs
  const selectedCategoryIds = useMemo(
    () => (searchRequest.categoryIds || []).map(Number),
    [searchRequest.categoryIds]
  );

  const selectedBrandIds = useMemo(
    () => (searchRequest.brandIds || []).map(Number),
    [searchRequest.brandIds]
  );

  const selectedAttrIds = useMemo(
    () => (searchRequest.attributeValueIds || []).map(Number),
    [searchRequest.attributeValueIds]
  );

  // 1. Sort Categories: Selected items at top
  const sortedCategories = useMemo(() => {
    if (!filters?.categories) return [];
    return [...filters.categories].sort((a: any, b: any) => {
      const aId = Number(a.id ?? a.categoryId ?? a.value);
      const bId = Number(b.id ?? b.categoryId ?? b.value);
      const aChecked = selectedCategoryIds.includes(aId);
      const bChecked = selectedCategoryIds.includes(bId);

      if (aChecked && !bChecked) return -1;
      if (!aChecked && bChecked) return 1;
      return 0;
    });
  }, [filters?.categories, selectedCategoryIds]);

  // 2. Sort Brands: Selected items at top
  const sortedBrands = useMemo(() => {
    if (!filters?.brands) return [];
    return [...filters.brands].sort((a: any, b: any) => {
      const aId = Number(a.id ?? a.brandId ?? a.value);
      const bId = Number(b.id ?? b.brandId ?? b.value);
      const aChecked = selectedBrandIds.includes(aId);
      const bChecked = selectedBrandIds.includes(bId);

      if (aChecked && !bChecked) return -1;
      if (!aChecked && bChecked) return 1;
      return 0;
    });
  }, [filters?.brands, selectedBrandIds]);

  const clearFilters = () => {
    setPriceRange(filters?.priceRange?.maxPrice ?? 0);

    onChange({
      ...searchRequest,
      brandIds: [],
      categoryIds: [],
      attributeValueIds: [],
      minPrice: undefined,
      maxPrice: undefined,
      onlyInStock: false,
      onlyOnSale: false,
      onlyNewArrivals: false,
      onlyBestSeller: false,
      onlyPetlandRecommended: false,
      pageNumber: 1,
      sortBy: "Relevance",
    });
  };

  const hasActiveFilters =
    selectedCategoryIds.length > 0 ||
    selectedBrandIds.length > 0 ||
    selectedAttrIds.length > 0 ||
    searchRequest.onlyOnSale ||
    searchRequest.onlyNewArrivals ||
    searchRequest.onlyBestSeller ||
    searchRequest.onlyPetlandRecommended ||
    (searchRequest.minRating ?? 0) >= 4 ||
    searchRequest.onlyInStock;

  return (
    <div className="w-full p-5 shadow-sm top-4 bg-white rounded-2xl border border-gray-100">
      {/* Header Actions */}
      <div className="flex items-center justify-between pb-4 mb-4 border-b border-gray-100">
        <div className="flex items-center gap-2 font-bold text-gray-800 text-lg">
          <SlidersHorizontal size={18} className="text-gray-600" />
          <span>Filters</span>
        </div>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-red-500 transition cursor-pointer"
          >
            <RotateCcw size={12} />
            Clear All
          </button>
        )}
      </div>

      {/* Selected Chips at the Top */}
      {hasActiveFilters && (
        <div className="mb-4 pb-3 border-b border-gray-100">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
            Selected
          </div>
          <div className="flex flex-wrap gap-1.5">
            {/* Category Chips */}
            {filters?.categories
              ?.filter((c: any) =>
                selectedCategoryIds.includes(Number(c.id ?? c.categoryId ?? c.value))
              )
              .map((c: any) => {
                const id = Number(c.id ?? c.categoryId ?? c.value);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200 rounded-full"
                  >
                    {c.name ?? c.label}
                    <button
                      type="button"
                      onClick={() =>
                        onChange({
                          ...searchRequest,
                          categoryIds: selectedCategoryIds.filter((x) => x !== id),
                          pageNumber: 1,
                        })
                      }
                      className="text-amber-600 hover:text-amber-900"
                    >
                      <X size={12} />
                    </button>
                  </span>
                );
              })}

            {/* Brand Chips */}
            {filters?.brands
              ?.filter((b: any) =>
                selectedBrandIds.includes(Number(b.id ?? b.brandId ?? b.value))
              )
              .map((b: any) => {
                const id = Number(b.id ?? b.brandId ?? b.value);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200 rounded-full"
                  >
                    {b.name ?? b.label}
                    <button
                      type="button"
                      onClick={() =>
                        onChange({
                          ...searchRequest,
                          brandIds: selectedBrandIds.filter((x) => x !== id),
                          pageNumber: 1,
                        })
                      }
                      className="text-slate-500 hover:text-slate-800"
                    >
                      <X size={12} />
                    </button>
                  </span>
                );
              })}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* 1. Category */}
        {sortedCategories.length > 0 && (
          <details className="group border-b border-gray-100 pb-4" open>
            <summary className="flex items-center justify-between font-semibold text-gray-700 cursor-pointer list-none">
              <span>Category</span>
              <ChevronDown
                size={16}
                className="text-gray-400 transition-transform group-open:rotate-180"
              />
            </summary>
            <div className="mt-3 space-y-2.5 pl-1 max-h-52 overflow-y-auto">
              {sortedCategories.map((cat: any) => {
                const targetCatId = Number(cat.id ?? cat.categoryId ?? cat.value);
                const isSelected = selectedCategoryIds.includes(targetCatId);

                return (
                  <label
                    key={targetCatId || cat.name}
                    className={`flex items-center gap-3 text-sm cursor-pointer transition ${
                      isSelected
                        ? "text-amber-900 font-medium"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const newCategoryIds: number[] = e.target.checked
                          ? [...selectedCategoryIds, targetCatId]
                          : selectedCategoryIds.filter((id) => id !== targetCatId);

                        onChange({
                          ...searchRequest,
                          categoryIds: newCategoryIds,
                          pageNumber: 1,
                        });
                      }}
                      className="w-4 h-4 rounded border-gray-300 accent-amber-500"
                    />
                    <div className="flex justify-between w-full">
                      <span>{cat.name ?? cat.label}</span>
                      <span
                        className={`text-xs ${
                          isSelected ? "text-amber-700" : "text-gray-400"
                        }`}
                      >
                        ({cat.count})
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>
          </details>
        )}

        {/* 2. Price Range */}
        {filters?.priceRange && (
          <details className="group border-b border-gray-100 pb-4" open>
            <summary className="flex items-center justify-between font-semibold text-gray-700 cursor-pointer list-none">
              <span>Price Range</span>
              <ChevronDown
                size={16}
                className="text-gray-400 transition-transform group-open:rotate-180"
              />
            </summary>
            <div className="mt-3 pl-1">
              <input
                type="range"
                min={filters.priceRange.minPrice ?? 0}
                max={filters.priceRange.maxPrice ?? 0}
                value={priceRange}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setPriceRange(value);
                  onChange({
                    ...searchRequest,
                    maxPrice: value,
                    pageNumber: 1,
                  });
                }}
                className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-500"
              />
              <div className="flex justify-between items-center mt-2 text-xs text-gray-500 font-medium">
                AED {filters.priceRange.minPrice ?? 0}
                <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded border border-amber-100">
                  Max: AED {priceRange}
                </span>
              </div>
            </div>
          </details>
        )}

        {/* 3. Brands */}
        {sortedBrands.length > 0 && (
          <details open className="group border-b border-gray-100 pb-4">
            <summary className="flex cursor-pointer items-center justify-between font-semibold text-gray-700 list-none">
              <span>Brands</span>
              <ChevronDown className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180" />
            </summary>

            <div className="mt-3 space-y-2.5 pl-1 max-h-48 overflow-y-auto">
              {sortedBrands.map((brand: any) => {
                const targetBrandId = Number(brand.id ?? brand.brandId ?? brand.value);
                const isSelected = selectedBrandIds.includes(targetBrandId);

                return (
                  <label
                    key={targetBrandId || brand.name}
                    className={`flex items-center justify-between text-sm cursor-pointer transition ${
                      isSelected
                        ? "text-amber-900 font-medium"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const newBrandIds: number[] = e.target.checked
                            ? [...selectedBrandIds, targetBrandId]
                            : selectedBrandIds.filter((id) => id !== targetBrandId);

                          onChange({
                            ...searchRequest,
                            brandIds: newBrandIds,
                            pageNumber: 1,
                          });
                        }}
                        className="w-4 h-4 rounded border-gray-300 accent-amber-500"
                      />
                      <span>{brand.name ?? brand.label}</span>
                    </div>
                    <span
                      className={`text-xs ${
                        isSelected ? "text-amber-700" : "text-gray-400"
                      }`}
                    >
                      ({brand.count})
                    </span>
                  </label>
                );
              })}
            </div>
          </details>
        )}

        {/* 4. Dynamic Attribute Filters */}
        {filters?.filters?.map((group: any) => {
          const sortedValues = [...(group.values || [])].sort((a: any, b: any) => {
            const aId = Number(a.id ?? a.valueId ?? a.value);
            const bId = Number(b.id ?? b.valueId ?? b.value);
            const aChecked = selectedAttrIds.includes(aId);
            const bChecked = selectedAttrIds.includes(bId);

            if (aChecked && !bChecked) return -1;
            if (!aChecked && bChecked) return 1;
            return 0;
          });

          return (
            <details
              key={group.attributeId || group.title}
              open
              className="group border-b border-gray-100 pb-4"
            >
              <summary className="flex items-center justify-between font-semibold text-gray-700 cursor-pointer list-none">
                <span>{group.title}</span>
                <ChevronDown
                  size={16}
                  className="text-gray-400 transition-transform group-open:rotate-180"
                />
              </summary>

              <div className="mt-3 space-y-2.5 pl-1">
                {sortedValues.map((value: any) => {
                  const targetValueId = Number(value.id ?? value.valueId ?? value.value);
                  const isSelected = selectedAttrIds.includes(targetValueId);

                  return (
                    <label
                      key={targetValueId || value.name}
                      className={`flex items-center justify-between text-sm cursor-pointer transition ${
                        isSelected
                          ? "text-amber-900 font-medium"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const newAttrIds: number[] = e.target.checked
                              ? [...selectedAttrIds, targetValueId]
                              : selectedAttrIds.filter((id) => id !== targetValueId);

                            onChange({
                              ...searchRequest,
                              attributeValueIds: newAttrIds,
                              pageNumber: 1,
                            });
                          }}
                          className="w-4 h-4 rounded border-gray-300 accent-amber-500"
                        />
                        <span>{value.name ?? value.label}</span>
                      </div>
                      <span
                        className={`text-xs ${
                          isSelected ? "text-amber-700" : "text-gray-400"
                        }`}
                      >
                        ({value.count})
                      </span>
                    </label>
                  );
                })}
              </div>
            </details>
          );
        })}

        {/* 5. Offers, Highlights & Ratings */}
        <details className="group border-b border-gray-100 pb-4" open>
          <summary className="flex items-center justify-between font-semibold text-gray-700 cursor-pointer list-none">
            <span>Offers & Highlights</span>
            <ChevronDown
              size={16}
              className="text-gray-400 transition-transform group-open:rotate-180"
            />
          </summary>

          <div className="mt-3 space-y-2.5 pl-1">
            <label className="flex items-center gap-3 text-sm text-gray-600 cursor-pointer hover:text-gray-900">
              <input
                type="checkbox"
                checked={!!searchRequest.onlyOnSale}
                onChange={(e) =>
                  onChange({
                    ...searchRequest,
                    onlyOnSale: e.target.checked,
                    pageNumber: 1,
                  })
                }
                className="w-4 h-4 rounded border-gray-300 accent-amber-500"
              />
              <span>On Sale / Discounted</span>
            </label>

            <label className="flex items-center gap-3 text-sm text-gray-600 cursor-pointer hover:text-gray-900">
              <input
                type="checkbox"
                checked={!!searchRequest.onlyNewArrivals}
                onChange={(e) =>
                  onChange({
                    ...searchRequest,
                    onlyNewArrivals: e.target.checked,
                    newArrivalOnly: e.target.checked,
                    pageNumber: 1,
                  })
                }
                className="w-4 h-4 rounded border-gray-300 accent-amber-500"
              />
              <span>New Arrivals</span>
            </label>

            <label className="flex items-center gap-3 text-sm text-gray-600 cursor-pointer hover:text-gray-900">
              <input
                type="checkbox"
                checked={!!searchRequest.onlyBestSeller}
                onChange={(e) =>
                  onChange({
                    ...searchRequest,
                    onlyBestSeller: e.target.checked,
                    bestSellerOnly: e.target.checked,
                    pageNumber: 1,
                  })
                }
                className="w-4 h-4 rounded border-gray-300 accent-amber-500"
              />
              <span>Best Seller</span>
            </label>

            <label className="flex items-center gap-3 text-sm text-gray-600 cursor-pointer hover:text-gray-900">
              <input
                type="checkbox"
                checked={!!searchRequest.onlyPetlandRecommended}
                onChange={(e) =>
                  onChange({
                    ...searchRequest,
                    onlyPetlandRecommended: e.target.checked,
                    pageNumber: 1,
                  })
                }
                className="w-4 h-4 rounded border-gray-300 accent-amber-500"
              />
              <span>Petland Recommended</span>
            </label>

            <label className="flex items-center gap-3 text-sm text-gray-600 cursor-pointer hover:text-gray-900">
              <input
                type="checkbox"
                checked={(searchRequest.minRating ?? 0) >= 4}
                onChange={(e) =>
                  onChange({
                    ...searchRequest,
                    minRating: e.target.checked ? 4 : 0,
                    pageNumber: 1,
                  })
                }
                className="w-4 h-4 rounded border-gray-300 accent-amber-500"
              />
              <span>4★ & Above</span>
            </label>
          </div>
        </details>

        {/* 6. Stock Availability Toggle */}
        <div className="pt-2 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">
            In Stock Only
          </span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={!!searchRequest.onlyInStock}
              onChange={(e) =>
                onChange({
                  ...searchRequest,
                  onlyInStock: e.target.checked,
                  pageNumber: 1,
                })
              }
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500"></div>
          </label>
        </div>
      </div>
    </div>
  );
};

export default FilterSidebar;